"use strict";
/*
This file is part of web3.js.

web3.js is free software: you can redistribute it and/or modify
it under the terms of the GNU Lesser General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

web3.js is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Lesser General Public License for more details.

You should have received a copy of the GNU Lesser General Public License
along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseTransaction = void 0;
const web3_utils_1 = require("@theqrl/web3-utils");
const web3_validator_1 = require("@theqrl/web3-validator");
const constants_js_1 = require("./constants.js");
const index_js_1 = require("../common/index.js");
const qrl_wallet_js_1 = require("../qrl_wallet.js");
const address_js_1 = require("./address.js");
const utils_js_1 = require("./utils.js");
/**
 * This base class will likely be subject to further
 * refactoring along the introduction of additional tx types
 * on the QRL network.
 *
 * It is therefore not recommended to use directly.
 */
class BaseTransaction {
    constructor(txData, opts) {
        var _a, _b;
        this.cache = {
            hash: undefined,
            dataFee: undefined,
        };
        /**
         * The default chain the tx falls back to if no Common
         * is provided and if the chain can't be derived from
         * a passed in chainId (only EIP-2718 typed txs) or
         * EIP-155 signature (legacy txs).
         *
         * @hidden
         */
        this.DEFAULT_CHAIN = index_js_1.Chain.Mainnet;
        /**
         * The default HF if the tx type is active on that HF
         * or the first greater HF where the tx is active.
         *
         * @hidden
         */
        this.DEFAULT_HARDFORK = index_js_1.Hardfork.Zond;
        const { nonce, gasLimit, to, value, data, descriptor, extraParams, signature, publicKey, type } = txData;
        this._type = Number((0, index_js_1.uint8ArrayToBigInt)((0, index_js_1.toUint8Array)(type)));
        this.txOptions = opts;
        let toB;
        if (typeof to === 'string') {
            if (to === '') {
                toB = (0, index_js_1.toUint8Array)('0x');
            }
            else if ((0, web3_validator_1.isAddressString)(to)) {
                toB = (0, index_js_1.toUint8Array)((0, web3_utils_1.toHex)(to));
            }
            else {
                throw new Error(`Cannot convert string to Uint8Array. only supports address strings and this string was given: ${to}`);
            }
        }
        else {
            toB = (0, index_js_1.toUint8Array)(to);
        }
        const descriptorB = (0, index_js_1.toUint8Array)(descriptor === '' ? '0x' : descriptor);
        const extraParamsB = (0, index_js_1.toUint8Array)(extraParams === '' ? '0x' : extraParams);
        const signatureB = (0, index_js_1.toUint8Array)(signature === '' ? '0x' : signature);
        const publicKeyB = (0, index_js_1.toUint8Array)(publicKey === '' ? '0x' : publicKey);
        this.nonce = (0, index_js_1.uint8ArrayToBigInt)((0, index_js_1.toUint8Array)(nonce === '' ? '0x' : nonce));
        this.gasLimit = (0, index_js_1.uint8ArrayToBigInt)((0, index_js_1.toUint8Array)(gasLimit === '' ? '0x' : gasLimit));
        this.to = toB.length > 0 ? new address_js_1.Address(toB) : undefined;
        this.value = (0, index_js_1.uint8ArrayToBigInt)((0, index_js_1.toUint8Array)(value === '' ? '0x' : value));
        this.data = (0, index_js_1.toUint8Array)(data === '' ? '0x' : data);
        this.descriptor = descriptorB.length > 0 ? descriptorB : undefined;
        this.extraParams = extraParamsB.length > 0 ? extraParamsB : undefined;
        this.signature = signatureB.length > 0 ? signatureB : undefined;
        this.publicKey = publicKeyB.length > 0 ? publicKeyB : undefined;
        this._validateCannotExceedMaxInteger({ value: this.value });
        // gqrl limits gasLimit to 2^64-1
        this._validateCannotExceedMaxInteger({ gasLimit: this.gasLimit }, 64);
        // EIP-2681 limits nonce to 2^64-1 (cannot equal 2^64-1)
        this._validateCannotExceedMaxInteger({ nonce: this.nonce }, 64, true);
        // eslint-disable-next-line no-null/no-null
        const createContract = this.to === undefined || this.to === null;
        const allowUnlimitedInitCodeSize = (_a = opts.allowUnlimitedInitCodeSize) !== null && _a !== void 0 ? _a : false;
        const common = (_b = opts.common) !== null && _b !== void 0 ? _b : this._getCommon();
        if (createContract && !allowUnlimitedInitCodeSize) {
            (0, utils_js_1.checkMaxInitCodeSize)(common, this.data.length);
        }
    }
    /**
     * Returns the transaction type.
     */
    get type() {
        return this._type;
    }
    validate(stringError = false) {
        const errors = [];
        if (this.getBaseFee() > this.gasLimit) {
            errors.push(`gasLimit is too low. given ${this.gasLimit}, need at least ${this.getBaseFee()}`);
        }
        if (this.isSigned() && !this.verifySignature()) {
            errors.push('Invalid Signature');
        }
        return stringError ? errors : errors.length === 0;
    }
    /**
     * The minimum amount of gas the tx must have (DataFee + TxFee + Creation Fee)
     */
    getBaseFee() {
        const txFee = this.common.param('gasPrices', 'tx');
        let fee = this.getDataFee();
        if (txFee)
            fee += txFee;
        if (this.toCreationAddress()) {
            const txCreationFee = this.common.param('gasPrices', 'txCreation');
            if (txCreationFee)
                fee += txCreationFee;
        }
        return fee;
    }
    /**
     * The amount of gas paid for the data in this tx
     */
    getDataFee() {
        const txDataZero = this.common.param('gasPrices', 'txDataZero');
        const txDataNonZero = this.common.param('gasPrices', 'txDataNonZero');
        let cost = BigInt(0);
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < this.data.length; i += 1) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, no-unused-expressions
            this.data[i] === 0 ? (cost += txDataZero) : (cost += txDataNonZero);
        }
        // eslint-disable-next-line no-null/no-null
        if (this.to === undefined || this.to === null) {
            const dataLength = BigInt(Math.ceil(this.data.length / 32));
            const initCodeCost = this.common.param('gasPrices', 'initCodeWordCost') * dataLength;
            cost += initCodeCost;
        }
        return cost;
    }
    /**
     * If the tx's `to` is to the creation address
     */
    toCreationAddress() {
        return this.to === undefined || this.to.buf.length === 0;
    }
    isSigned() {
        const { descriptor, signature, publicKey } = this;
        if (descriptor === undefined ||
            signature === undefined ||
            publicKey === undefined) {
            return false;
        }
        return true;
    }
    /**
     * Determines if the signature is valid
     */
    verifySignature() {
        const msgHash = this.getMessageToVerifySignature();
        const { descriptor, signature, publicKey } = this;
        try {
            const desc = (0, qrl_wallet_js_1.descriptorFromBytes)(descriptor);
            switch (desc.type()) {
                case qrl_wallet_js_1.qrlWalletType.ML_DSA_87:
                    return (0, qrl_wallet_js_1.verifyMLDSA87Signature)(signature, msgHash, publicKey);
                default:
                    return false;
            }
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Returns the sender's address
     */
    getSenderAddress() {
        const { descriptor, publicKey } = this;
        return new address_js_1.Address(address_js_1.Address.publicKeyAndDescriptorToAddress(publicKey, descriptor));
    }
    /**
     * Signs a transaction.
     *
     * Note that the signed tx is returned as a new object,
     * use as follows:
     * ```javascript
     * const signedTx = tx.sign(privateKey, publicKey)
     * ```
     */
    sign(seed) {
        if (seed.length !== constants_js_1.SEED_BYTES) {
            const msg = this._errorMsg(`Seed must be ${constants_js_1.SEED_BYTES} bytes in length.`);
            throw new Error(msg);
        }
        const wallet = (0, qrl_wallet_js_1.newMLDSA87WalletFromExtendedSeed)(seed);
        const descBytes = wallet.getDescriptor().toBytes();
        const extraParamsBytes = Uint8Array.from([]);
        const msgHash = this.getMessageToSign(descBytes, extraParamsBytes, true);
        const signature = wallet.sign(msgHash);
        const tx = this._processAuthValues(descBytes, extraParamsBytes, signature, wallet.getPK());
        return tx;
    }
    /**
     * Does chain ID checks on common and returns a common
     * to be used on instantiation
     * @hidden
     *
     * @param common - {@link Common} instance from tx options
     * @param chainId - Chain ID from tx options (typed txs) or signature (legacy tx)
     */
    _getCommon(common, chainId) {
        var _a;
        // Chain ID provided
        if (chainId !== undefined) {
            const chainIdBigInt = (0, index_js_1.uint8ArrayToBigInt)((0, index_js_1.toUint8Array)(chainId));
            if (common) {
                if (common.chainId() !== chainIdBigInt) {
                    const msg = this._errorMsg('The chain ID does not match the chain ID of Common');
                    throw new Error(msg);
                }
                // Common provided, chain ID does match
                // -> Return provided Common
                return common.copy();
            }
            if (index_js_1.Common.isSupportedChainId(chainIdBigInt)) {
                // No Common, chain ID supported by Common
                // -> Instantiate Common with chain ID
                return new index_js_1.Common({ chain: chainIdBigInt, hardfork: this.DEFAULT_HARDFORK });
            }
            // No Common, chain ID not supported by Common
            // -> Instantiate custom Common derived from DEFAULT_CHAIN
            return index_js_1.Common.custom({
                name: 'custom-chain',
                networkId: chainIdBigInt,
                chainId: chainIdBigInt,
            }, { baseChain: this.DEFAULT_CHAIN, hardfork: this.DEFAULT_HARDFORK });
        }
        // No chain ID provided
        // -> return Common provided or create new default Common
        return ((_a = common === null || common === void 0 ? void 0 : common.copy()) !== null && _a !== void 0 ? _a : new index_js_1.Common({ chain: this.DEFAULT_CHAIN, hardfork: this.DEFAULT_HARDFORK }));
    }
    /**
     * Validates that an object with BigInt values cannot exceed the specified bit limit.
     * @param values Object containing string keys and BigInt values
     * @param bits Number of bits to check (64 or 256)
     * @param cannotEqual Pass true if the number also cannot equal one less the maximum value
     */
    _validateCannotExceedMaxInteger(values, bits = 256, cannotEqual = false) {
        for (const [key, value] of Object.entries(values)) {
            switch (bits) {
                case 64:
                    if (cannotEqual) {
                        if (value !== undefined && value >= constants_js_1.MAX_UINT64) {
                            const msg = this._errorMsg(`${key} cannot equal or exceed MAX_UINT64 (2^64-1), given ${value}`);
                            throw new Error(msg);
                        }
                    }
                    else if (value !== undefined && value > constants_js_1.MAX_UINT64) {
                        const msg = this._errorMsg(`${key} cannot exceed MAX_UINT64 (2^64-1), given ${value}`);
                        throw new Error(msg);
                    }
                    break;
                case 256:
                    if (cannotEqual) {
                        if (value !== undefined && value >= constants_js_1.MAX_INTEGER) {
                            const msg = this._errorMsg(`${key} cannot equal or exceed MAX_INTEGER (2^256-1), given ${value}`);
                            throw new Error(msg);
                        }
                    }
                    else if (value !== undefined && value > constants_js_1.MAX_INTEGER) {
                        const msg = this._errorMsg(`${key} cannot exceed MAX_INTEGER (2^256-1), given ${value}`);
                        throw new Error(msg);
                    }
                    break;
                default: {
                    const msg = this._errorMsg('unimplemented bits value');
                    throw new Error(msg);
                }
            }
        }
    }
    static _validateNotArray(values) {
        const txDataKeys = [
            'nonce',
            'gasLimit',
            'to',
            'value',
            'data',
            'type',
            'baseFee',
            'maxFeePerGas',
            'chainId',
        ];
        for (const [key, value] of Object.entries(values)) {
            if (txDataKeys.includes(key)) {
                if (Array.isArray(value)) {
                    throw new Error(`${key} cannot be an array`);
                }
            }
        }
    }
    /**
     * Returns the shared error postfix part for _error() method
     * tx type implementations.
     */
    _getSharedErrorPostfix() {
        let hash = '';
        try {
            hash = this.isSigned() ? (0, web3_utils_1.bytesToHex)(this.hash()) : 'not available (unsigned)';
        }
        catch (e) {
            hash = 'error';
        }
        let isSigned = '';
        try {
            isSigned = this.isSigned().toString();
        }
        catch (e) {
            hash = 'error';
        }
        let hf = '';
        try {
            hf = this.common.hardfork();
        }
        catch (e) {
            hf = 'error';
        }
        let postfix = `tx type=${this.type} hash=${hash} nonce=${this.nonce} value=${this.value} `;
        postfix += `signed=${isSigned} hf=${hf}`;
        return postfix;
    }
}
exports.BaseTransaction = BaseTransaction;
//# sourceMappingURL=baseTransaction.js.map